import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FolderInput, Languages, Loader2, Percent, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { clearSelection, isAdminUser, subscribeSelection } from '../utils/adminSelection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Click dreapta pe orice produs (cont de admin) → mutare rapidă în altă categorie,
 *  individual sau pentru toate produsele bifate. */
export const AdminQuickMove = () => {
  const { toast } = useToast();
  const [target, setTarget] = useState(null);     // {id, name} sau {bulk: true}
  const [info, setInfo] = useState(null);
  const [cats, setCats] = useState([]);
  const [selection, setSelection] = useState(new Map());
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [margin, setMargin] = useState(null);      // date marjă pentru produsul selectat
  const [groupInput, setGroupInput] = useState('');
  const [prodInput, setProdInput] = useState('');

  useEffect(() => subscribeSelection(setSelection), []);

  useEffect(() => {
    const onContextMenu = (e) => {
      if (!isAdminUser()) return;
      const el = e.target.closest?.('[data-product-id]');
      if (!el) return;
      e.preventDefault();
      setTarget({ id: el.getAttribute('data-product-id'), name: el.getAttribute('data-product-name') || '' });
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, []);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);

  const loadCats = useCallback(() => {
    if (!cats.length) axios.get(`${API}/categories`).then((r) => setCats(r.data || [])).catch(() => {});
  }, [cats.length]);

  useEffect(() => {
    if (!target) { setInfo(null); setQ(''); setMargin(null); setGroupInput(''); setProdInput(''); return; }
    loadCats();
    if (target.bulk) { setInfo(null); return; }
    axios.get(`${API}/products/${target.id}/move-info`, auth())
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ id: target.id, name: target.name }));
    axios.get(`${API}/products/${target.id}/margin-info`, auth())
      .then((r) => {
        setMargin(r.data);
        setGroupInput(r.data.groupMarkup ?? r.data.effectiveGroupMarkup ?? '');
        setProdInput(r.data.productMarkup ?? r.data.currentMarkup ?? '');
      })
      .catch(() => setMargin(null));
  }, [target, auth, loadCats]);

  const options = useMemo(() => {
    const byId = {};
    cats.forEach((c) => { byId[String(c._id)] = c; });
    const path = (c) => {
      const parts = [c.name];
      let p = c.parentId ? byId[String(c.parentId)] : null;
      let guard = 0;
      while (p && guard++ < 5) { parts.unshift(p.name); p = p.parentId ? byId[String(p.parentId)] : null; }
      return parts.join(' › ');
    };
    const list = cats.filter((c) => c.isActive !== false).map((c) => ({ slug: c.slug, label: path(c) }));
    const needle = q.trim().toLowerCase();
    const filtered = needle ? list.filter((c) => c.label.toLowerCase().includes(needle)) : list;
    return filtered.sort((a, b) => a.label.localeCompare(b.label, 'ro')).slice(0, 60);
  }, [cats, q]);

  const finish = (moved, categoryName, alsoClear) => {
    toast({ title: 'Mutat', description: `${moved} produs(e) în „${categoryName}”` });
    if (alsoClear) clearSelection();
    setTarget(null);
  };

  const handlePick = async (slug) => {
    setBusy(true);
    try {
      if (target.bulk) {
        const r = await axios.post(`${API}/products/bulk-move-category`,
          { productIds: [...selection.keys()], categorySlug: slug }, auth());
        finish(r.data.moved, r.data.categoryName, true);
        return;
      }
      const siblings = info?.supplierCategoryCount || 0;
      const scope = siblings > 1 && window.confirm(
        `Muți și celelalte ${siblings - 1} produse din categoria de furnizor „${info.supplierCategory}”?\n\n`
        + 'OK = mut toate · Anulează = mut doar acest produs') ? 'supplier_category' : 'single';
      const r = await axios.post(`${API}/products/${target.id}/move-category`,
        { categorySlug: slug, scope }, auth());
      finish(r.data.moved, r.data.categoryName, false);
    } catch (e) {
      toast({ title: 'Eroare', description: e.response?.data?.detail || 'Mutarea a eșuat', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const saveGroupMargin = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/products/${target.id}/margin/group`,
        { markup: Number(groupInput) }, auth());
      toast({ title: 'Marjă grupă salvată', description: `„${r.data.group}”: ${r.data.markup}% · ${r.data.repriced} produse repreţuite` });
      setTarget(null);
    } catch (e) {
      toast({ title: 'Eroare', description: e.response?.data?.detail || 'Nu s-a putut salva marja', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const saveProductMargin = async (reset = false) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/products/${target.id}/margin`,
        { markup: reset ? null : Number(prodInput) }, auth());
      toast({
        title: reset ? 'Marjă proprie ștearsă' : 'Marjă produs salvată',
        description: reset ? `Revine la marja grupei (${r.data.markup}%)` : `${r.data.markup}% · preț nou ${r.data.price} Lei`,
      });
      setTarget(null);
    } catch (e) {
      toast({ title: 'Eroare', description: e.response?.data?.detail || 'Nu s-a putut salva marja', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleTranslate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/products/${target.id}/translate`, {}, auth());
      toast({ title: 'Tradus', description: r.data.name || 'Produsul a fost tradus în română' });
      setTarget(null);
      window.location.reload();
    } catch (e) {
      toast({ title: 'Traducerea a eșuat', description: e.response?.data?.detail || 'Încearcă din nou', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleBulkTranslate = async () => {
    if (!window.confirm(`Traduci în română ${selection.size} produse selectate? Rulează în fundal, câteva secunde/produs.`)) return;
    try {
      const r = await axios.post(`${API}/products/bulk-translate`, { productIds: [...selection.keys()] }, auth());
      localStorage.setItem('bulkTranslateJob', r.data.jobId);
      setJob({ jobId: r.data.jobId, total: r.data.total, processed: 0, done: 0, failed: 0, status: 'running' });
      clearSelection();
    } catch (e) {
      toast({ title: 'Eroare', description: e.response?.data?.detail || 'Nu s-a putut porni traducerea', variant: 'destructive' });
    }
  };

  useEffect(() => {
    const jobId = job?.jobId || localStorage.getItem('bulkTranslateJob');
    if (!jobId || job?.status === 'finished' || !isAdminUser()) return undefined;
    const tick = () => axios.get(`${API}/products/bulk-translate/${jobId}`, auth())
      .then((r) => {
        setJob(r.data);
        if (r.data.status === 'finished') {
          localStorage.removeItem('bulkTranslateJob');
          toast({ title: 'Traducere finalizată', description: `${r.data.done} traduse, ${r.data.failed} eșuate` });
        }
      })
      .catch(() => { localStorage.removeItem('bulkTranslateJob'); setJob(null); });
    tick();
    const t = setInterval(tick, 3000);
    return () => clearInterval(t);
  }, [job?.jobId, job?.status, auth, toast]);

  return (
    <>
      {job && (
        <div className="fixed bottom-36 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs text-green-900 shadow-2xl ring-1 ring-green-200 lg:bottom-20"
          data-testid="bulk-translate-progress">
          {job.status === 'finished'
            ? <Languages className="h-4 w-4 text-green-700" />
            : <Loader2 className="h-4 w-4 animate-spin text-green-700" />}
          <span data-testid="bulk-translate-progress-text">
            {job.status === 'finished' ? 'Traducere gata' : 'Se traduce'}: {job.processed}/{job.total}
            {job.failed ? ` · ${job.failed} eșuate` : ''}
          </span>
          {job.status === 'finished' && (
            <>
              <button className="font-semibold underline" onClick={() => window.location.reload()}
                data-testid="bulk-translate-refresh">Reîncarcă</button>
              <button onClick={() => setJob(null)} aria-label="Închide" data-testid="bulk-translate-close">
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      )}

      {selection.size > 0 && (
        <div className="fixed bottom-20 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-2 rounded-2xl bg-green-800 px-3 py-2 text-white shadow-2xl lg:bottom-6"
          data-testid="admin-selection-bar">
          <span className="text-xs font-semibold" data-testid="admin-selection-count">
            {selection.size} produse selectate
          </span>
          <Button size="sm" className="h-8 bg-white text-xs text-green-900 hover:bg-green-50"
            onClick={() => setTarget({ bulk: true })} data-testid="admin-selection-move">
            <FolderInput className="mr-1 h-3 w-3" /> Mută în categorie
          </Button>
          <Button size="sm" className="h-8 bg-white text-xs text-green-900 hover:bg-green-50"
            disabled={job?.status === 'running'} onClick={handleBulkTranslate} data-testid="admin-selection-translate">
            <Languages className="mr-1 h-3 w-3" /> Traduce în bloc
          </Button>
          <button onClick={clearSelection} className="p-1 text-white/80 hover:text-white" aria-label="Golește selecția"
            data-testid="admin-selection-clear">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {target && (
        <Dialog open onOpenChange={(o) => !o && setTarget(null)}>
          <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="admin-quick-move-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <FolderInput className="h-5 w-5 text-green-700" />
                {target.bulk ? `Mută ${selection.size} produse selectate` : 'Mută produsul în altă categorie'}
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500">
                Caută și alege categoria în care vrei să ajungă {target.bulk ? 'produsele selectate' : 'produsul'}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-xl bg-green-50 p-3 text-xs text-green-900" data-testid="admin-quick-move-product">
                {target.bulk ? (
                  <p className="font-semibold">{[...selection.values()].slice(0, 3).join(' · ')}
                    {selection.size > 3 ? ` și încă ${selection.size - 3}` : ''}</p>
                ) : (
                  <>
                    <p className="font-semibold">{info?.name || target.name}</p>
                    <p>Categorie actuală: <b>{info?.categoryName || info?.categorySlug || '—'}</b></p>
                    {info?.supplierCategory && (
                      <p>Categorie furnizor: <b>{info.supplierCategory}</b> ({info.supplierCategoryCount} produse)</p>
                    )}
                  </>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută categoria..."
                  className="pl-8 text-sm" data-testid="admin-quick-move-search" />
              </div>
              <div className="max-h-48 overflow-y-auto rounded-xl border" data-testid="admin-quick-move-list">
                {options.map((c) => (
                  <button key={c.slug} disabled={busy} onClick={() => handlePick(c.slug)}
                    className="block w-full border-b px-3 py-2 text-left text-xs last:border-0 hover:bg-green-50 disabled:opacity-50"
                    data-testid={`admin-quick-move-option-${c.slug}`}>
                    {c.label}
                  </button>
                ))}
                {!options.length && <p className="p-3 text-xs text-gray-500">Nicio categorie găsită</p>}
              </div>
              {busy && <p className="flex items-center gap-2 text-xs text-gray-600"><Loader2 className="h-3 w-3 animate-spin" /> Se procesează...</p>}
              {!target.bulk && margin && (
                <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="admin-margin-editor">
                  <p className="flex items-center gap-1 text-xs font-semibold text-amber-900">
                    <Percent className="h-3.5 w-3.5" /> Modifică marja de preț
                  </p>
                  {margin.basePrice > 0 ? (
                    <p className="text-[11px] text-amber-800">
                      Achiziție: <b>{margin.basePrice} Lei</b> · Preț site: <b>{margin.price} Lei</b>
                      {margin.vatRate ? ` (TVA ${margin.vatRate}%)` : ''}
                    </p>
                  ) : (
                    <p className="text-[11px] text-red-700" data-testid="admin-margin-no-base">
                      Produsul nu are preț de achiziție salvat — marja nu poate fi recalculată.
                    </p>
                  )}

                  <div className="space-y-1">
                    <label className="block text-[11px] font-semibold text-gray-700">
                      Marjă produs (prioritară) {margin.productMarkup != null ? `· acum ${margin.productMarkup}%` : ''}
                    </label>
                    <div className="flex gap-2">
                      <Input type="number" step="0.5" value={prodInput} onChange={(e) => setProdInput(e.target.value)}
                        placeholder="ex: 5" className="h-8 text-xs" data-testid="admin-margin-product-input" />
                      <Button size="sm" className="h-8 bg-amber-600 text-xs hover:bg-amber-700"
                        disabled={busy || prodInput === '' || !(margin.basePrice > 0)}
                        onClick={() => saveProductMargin(false)} data-testid="admin-margin-product-save">
                        Salvează
                      </Button>
                      {margin.productMarkup != null && (
                        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy}
                          onClick={() => saveProductMargin(true)} data-testid="admin-margin-product-reset">
                          Șterge
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[11px] font-semibold text-gray-700">
                      Marjă grupă furnizor: <b>{margin.group || '—'}</b>
                      {margin.groupCount ? ` (${margin.groupCount} produse)` : ''}
                    </label>
                    <div className="flex gap-2">
                      <Input type="number" step="0.5" value={groupInput} onChange={(e) => setGroupInput(e.target.value)}
                        placeholder="ex: 20" className="h-8 text-xs" disabled={!margin.group}
                        data-testid="admin-margin-group-input" />
                      <Button size="sm" className="h-8 bg-green-700 text-xs hover:bg-green-800"
                        disabled={busy || groupInput === '' || !margin.group}
                        onClick={saveGroupMargin} data-testid="admin-margin-group-save">
                        Aplică grupei
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {!target.bulk && (
                <Button className="w-full bg-green-700 text-xs hover:bg-green-800" disabled={busy}
                  onClick={handleTranslate} data-testid="admin-quick-move-translate">
                  <Languages className="mr-1 h-3 w-3" /> Traduce în română (AI)
                </Button>
              )}
              <Button variant="outline" className="w-full text-xs" onClick={() => setTarget(null)} data-testid="admin-quick-move-cancel">
                Renunță
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AdminQuickMove;
